'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { usePathname } from 'next/navigation';
import { solicitudService } from '@/lib/api/solicitudService';
import { Solicitud } from '@/types/solicitud';
import { SolicitudTable } from './SolicitudTable';
import { SolicitudDetail } from './SolicitudDetail';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import ErrorMessage from '@/components/common/ErrorMessage';
import { useAuth } from '@/hooks/useAuth';
import { userService } from '@/lib/api/userService';
import { User } from '@/types/user';

// Icons
const SearchIcon = () => (
  <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

// Función para remover acentos
const removeAccents = (str: string): string =>
  str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

// Campos disponibles para filtrado (igual que otros módulos)
const FILTER_FIELDS = [
  { value: 'nombre', label: 'Nombre Completo' },
  { value: 'clienteEmpresa', label: 'Empresa' },
  { value: 'producto', label: 'Producto' },
  { value: 'asesorNombre', label: 'Asesor' },
  { value: 'solicitudEstatus', label: 'Estatus' },
  { value: 'solicitudNumeroControl', label: 'Número de Control' },
  { value: 'montoSolicitado', label: 'Monto Solicitado' },
  { value: 'montoAutorizado', label: 'Monto Autorizado' },
  { value: 'clienteNuevoRenovacion', label: 'Nuevo/Renovación' },
  { value: 'regionNombre', label: 'Región' },
];

const getFieldValue = (solicitud: Solicitud, field: string): string => {
  if (field === 'nombre') {
    return `${solicitud.clienteNombre || ''} ${solicitud.clienteApellidoPaterno || ''} ${solicitud.clienteApellidoMaterno || ''}`.trim();
  }
  const value = (solicitud as any)[field];
  return value !== null && value !== undefined ? String(value) : '';
};

const matchesFilter = (solicitud: Solicitud, field: string, value: string): boolean => {
  const fieldValue = getFieldValue(solicitud, field);
  if (value === '*') return fieldValue.trim() !== '';
  if (value === '""' || value === '') return fieldValue.trim() === '';
  const normalizedValue = removeAccents(value.toLowerCase().trim());
  const normalizedFieldValue = removeAccents(fieldValue.toLowerCase());
  return normalizedFieldValue.includes(normalizedValue);
};

const HistoricoManagement: React.FC = () => {
  const { user: authUser } = useAuth();
  const pathname = usePathname();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [solicitudes, setSolicitudes] = useState<Solicitud[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [selectedSolicitud, setSelectedSolicitud] = useState<Solicitud | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterField, setFilterField] = useState<string>('nombre');
  const [filterValue, setFilterValue] = useState<string>('');

  const fetchSolicitudes = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await solicitudService.getAll();
      if (response.data) {
        setSolicitudes(response.data || []);
      } else {
        setError(response.error || 'Error al cargar las solicitudes');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al cargar las solicitudes');
      console.error('Error fetching solicitudes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const authSub = authUser?.sub ?? null;
  useEffect(() => {
    if (!authSub) return;
    let cancelled = false;
    userService.getUser(authSub).then((response) => {
      if (!cancelled && response.data) setCurrentUser(response.data);
    }).catch((err) => {
      if (!cancelled && process.env.NODE_ENV === 'development') console.error('Error cargando usuario:', err);
    });
    return () => { cancelled = true; };
  }, [authSub]);

  useEffect(() => {
    fetchSolicitudes();
  }, [pathname]);

  // Filtro por campo específico
  const baseList = useMemo(() => {
    let list = solicitudes;

    // Control de acceso por scope
    if (currentUser?.ownerID) {
      const userScope = (currentUser.scope || '').toLowerCase();
      if (userScope === 'self') {
        list = list.filter((s) =>
          s.ownerID === currentUser.ownerID || s.inheritedID === currentUser.ownerID
        );
      }
    }

    // Filtro por campo
    if (filterValue.trim() !== '') {
      list = list.filter(s => matchesFilter(s, filterField, filterValue));
    }

    // Ordenar por fechaContacto descendente (más nuevas primero)
    return list.sort((a, b) => {
      const fechaA = a.fechaContacto ? new Date(a.fechaContacto).getTime() : 0;
      const fechaB = b.fechaContacto ? new Date(b.fechaContacto).getTime() : 0;
      return fechaB - fechaA;
    });
  }, [solicitudes, currentUser, filterField, filterValue]);

  // Búsqueda general sobre baseList
  const filtered = useMemo(() => {
    if (searchTerm.trim() === '') return baseList;
    const term = removeAccents(searchTerm.toLowerCase());
    return baseList.filter(s => {
      const nombreCompleto = removeAccents(
        `${s.clienteNombre || ''} ${s.clienteApellidoPaterno || ''} ${s.clienteApellidoMaterno || ''}`.trim().toLowerCase()
      );
      const empresa = removeAccents((s.clienteEmpresa || '').toLowerCase());
      const control = removeAccents((s.solicitudNumeroControl || '').toLowerCase());
      const producto = removeAccents((s.producto || '').toLowerCase());
      const asesor = removeAccents((s.asesorNombre || '').toLowerCase());
      return nombreCompleto.includes(term) || empresa.includes(term) ||
             control.includes(term) || producto.includes(term) || asesor.includes(term);
    });
  }, [searchTerm, baseList]);

  const handleView = (solicitud: Solicitud) => {
    if (!solicitud.id) return;
    setError(null);
    setSelectedSolicitud(solicitud);
    setIsReadOnly(true);
    setIsDetailOpen(true);
  };

  const handleEdit = (solicitud: Solicitud) => {
    if (!solicitud.id) return;
    setError(null);
    setSelectedSolicitud(solicitud);
    setIsReadOnly(false);
    setIsDetailOpen(true);
  };

  const handleUpdateDetail = (updated: Solicitud) => {
    setSelectedSolicitud(updated);
    setSolicitudes(prev => prev.map(s => s.id === updated.id ? updated : s));
  };

  // Solo sysAdmin puede editar en Histórico
  const canEdit = currentUser?.acl?.toLowerCase() === 'sysadmin';

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 bg-white p-6 rounded-xl shadow-sm border border-gray-100">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Histórico</h1>
          <p className="text-sm text-gray-500 mt-1">
            Consulta y visualización de todas las solicitudes históricas. Sin restricción de estatus.
          </p>
          {!canEdit && (
            <p className="text-xs text-blue-600 mt-1">
              Modo consulta: usa el icono de ojo para ver el detalle completo
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={fetchSolicitudes} disabled={isLoading}>
            {isLoading ? 'Cargando…' : 'Recargar'}
          </Button>
        </div>
      </div>

      {error && <ErrorMessage message={error} onRetry={fetchSolicitudes} />}

      {/* Filtros */}
      <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end mb-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Campo</label>
            <select
              value={filterField}
              onChange={(e) => { setFilterField(e.target.value); setFilterValue(''); }}
              className="w-full px-3 py-2 border rounded-lg shadow-sm border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
            >
              {FILTER_FIELDS.map(field => (
                <option key={field.value} value={field.value}>{field.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Valor</label>
            <Input
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder='Use * para campo lleno, "" para vacío'
            />
          </div>
          <div>
            <Button variant="secondary" onClick={() => setFilterValue('')} className="w-full">
              Limpiar Filtro
            </Button>
          </div>
        </div>

        {/* Búsqueda General */}
        <div className="flex flex-col md:flex-row gap-3 items-start md:items-center justify-between">
          <div className="w-full md:w-2/3">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <SearchIcon />
              </div>
              <input
                type="text"
                placeholder="Buscar por nombre completo, empresa, control, producto o asesor (sin acentos)…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg leading-5 bg-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm transition-all"
              />
            </div>
          </div>
        </div>

        <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
          <div>
            Mostrando <span className="font-medium text-gray-700">{filtered.length}</span> de{' '}
            <span className="font-medium text-gray-700">{baseList.length}</span> solicitud(es)
            {currentUser?.scope === 'self' && ' (solo propias)'}
          </div>
          {filterValue && (
            <div className="text-gray-400">
              Filtro: {FILTER_FIELDS.find(f => f.value === filterField)?.label} = {filterValue}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64 bg-white rounded-xl shadow-sm border border-gray-100">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-600" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-10 text-center">
          <div className="text-sm font-semibold text-gray-900">Sin resultados</div>
          <div className="mt-1 text-sm text-gray-500">
            Ajusta los filtros o la búsqueda para encontrar solicitudes.
          </div>
          <div className="mt-4 flex justify-center gap-2">
            <Button variant="secondary" onClick={() => { setSearchTerm(''); setFilterValue(''); }}>
              Limpiar búsqueda
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <SolicitudTable
              solicitudes={filtered}
              onView={handleView}
              onEdit={canEdit ? handleEdit : undefined}
            />
          </div>
          <div className="text-right text-xs text-gray-400 px-2">
            Total: {filtered.length} solicitud(es)
            {currentUser?.scope === 'self' && ' (filtradas por acceso propio)'}
          </div>
        </>
      )}

      <SolicitudDetail
        isOpen={isDetailOpen}
        onClose={() => setIsDetailOpen(false)}
        solicitud={selectedSolicitud}
        onUpdate={handleUpdateDetail}
        modulo="historico"
        readOnly={isReadOnly}
      />
    </div>
  );
};

export default HistoricoManagement;
